import type { SxProps, Theme } from "@mui/material";

import { ActivityCategory, ActivityDifficulty } from "@/graphql/graphql";

export const activityCreatePageWrapSx: SxProps<Theme> = {
  width: "100%",
  maxWidth: "72rem",
  mx: "auto",
  px: 2,
  py: 0,
  height: "calc(100vh - 68px - 3.5rem)",
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

export const dialogActionsSx: SxProps<Theme> = { px: 3, pb: 2 };

export const activityCreatePageHeaderSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

export const activityCreatePageTitleSx: SxProps<Theme> = {
  m: 0,
  fontFamily: '"PT Serif", sans-serif',
  fontSize: 32,
  lineHeight: "40px",
  fontWeight: 400,
};

export const activityCreatePageActionsSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1,
};

/** Matches hba HTML: `rounded` (not pill), compact toolbar buttons */
export const activityCreatePrimaryButtonSx: SxProps<Theme> = {
  borderRadius: 1,
  px: 1.5,
  py: 0.75,
  fontSize: 14,
  fontWeight: 600,
  backgroundColor: "#4b5563",
  color: "#fff",
  "&:hover": {
    backgroundColor: "#374151",
  },
};

export const activityCreateSecondaryButtonSx: SxProps<Theme> = {
  borderRadius: 1,
  px: 1.5,
  py: 0.75,
  fontSize: 14,
  fontWeight: 600,
};

/** Full-width equal columns like Tailwind `flex gap-1` children `flex-1` */
export const activityCreateFullWidthToggleRowSx: SxProps<Theme> = {
  display: "flex",
  gap: 0.5,
  width: "100%",
};

type ToggleActiveColors = {
  backgroundColor: string;
  borderColor: string;
  color: string;
};

const CATEGORY_ACTIVE_COLORS: Record<
  "COGNITIVE_EXERCISE" | "COMPENSATORY_STRATEGY" | "LIFESTYLE_CHANGES",
  ToggleActiveColors
> = {
  COGNITIVE_EXERCISE: {
    backgroundColor: "#a855f7",
    borderColor: "#9333ea",
    color: "#ffffff",
  },
  COMPENSATORY_STRATEGY: {
    backgroundColor: "#f97316",
    borderColor: "#ea580c",
    color: "#ffffff",
  },
  LIFESTYLE_CHANGES: {
    backgroundColor: "#14b8a6",
    borderColor: "#0d9488",
    color: "#ffffff",
  },
};

const DIFFICULTY_ACTIVE_COLORS: Record<ActivityDifficulty, ToggleActiveColors> = {
  [ActivityDifficulty.Easy]: {
    backgroundColor: "#22c55e",
    borderColor: "#16a34a",
    color: "#ffffff",
  },
  [ActivityDifficulty.Medium]: {
    backgroundColor: "#facc15",
    borderColor: "#eab308",
    color: "#ffffff",
  },
  [ActivityDifficulty.Hard]: {
    backgroundColor: "#ef4444",
    borderColor: "#dc2626",
    color: "#ffffff",
  },
};

/** Selected toggles keep the same fill on hover (MUI default hover would wash them out). */
export const activityCreateFullWidthToggleButtonSx = (
  active: boolean,
  activeColors: ToggleActiveColors,
  inactiveSx: SxProps<Theme>
): SxProps<Theme> => ({
  flex: 1,
  borderRadius: 1,
  py: 0.75,
  px: 1,
  fontSize: 12,
  fontWeight: 600,
  textTransform: "none",
  minWidth: 0,
  border: "1px solid",
  ...(active
    ? {
        ...activeColors,
        boxShadow: "none",
        "&:hover": {
          backgroundColor: activeColors.backgroundColor,
          borderColor: activeColors.borderColor,
          color: activeColors.color,
          boxShadow: "none",
        },
      }
    : inactiveSx),
});

export const categoryToggleStyles = {
  [ActivityCategory.CognitiveExercise]: {
    active: CATEGORY_ACTIVE_COLORS.COGNITIVE_EXERCISE,
    inactive: {
      backgroundColor: "#faf5ff",
      borderColor: "#d8b4fe",
      color: "#7e22ce",
      "&:hover": { backgroundColor: "#f3e8ff" },
    },
  },
  [ActivityCategory.CompensatoryStrategy]: {
    active: CATEGORY_ACTIVE_COLORS.COMPENSATORY_STRATEGY,
    inactive: {
      backgroundColor: "#fff7ed",
      borderColor: "#fdba74",
      color: "#c2410c",
      "&:hover": { backgroundColor: "#ffedd5" },
    },
  },
  [ActivityCategory.LifestyleChanges]: {
    active: CATEGORY_ACTIVE_COLORS.LIFESTYLE_CHANGES,
    inactive: {
      backgroundColor: "#f0fdfa",
      borderColor: "#5eead4",
      color: "#0f766e",
      "&:hover": { backgroundColor: "#ccfbf1" },
    },
  },
} as const;

export const difficultyToggleStyles = {
  [ActivityDifficulty.Easy]: {
    active: DIFFICULTY_ACTIVE_COLORS[ActivityDifficulty.Easy],
    inactive: {
      backgroundColor: "#f0fdf4",
      borderColor: "#86efac",
      color: "#15803d",
      "&:hover": { backgroundColor: "#dcfce7" },
    },
  },
  [ActivityDifficulty.Medium]: {
    active: DIFFICULTY_ACTIVE_COLORS[ActivityDifficulty.Medium],
    inactive: {
      backgroundColor: "#fefce8",
      borderColor: "#fde047",
      color: "#a16207",
      "&:hover": { backgroundColor: "#fef9c3" },
    },
  },
  [ActivityDifficulty.Hard]: {
    active: DIFFICULTY_ACTIVE_COLORS[ActivityDifficulty.Hard],
    inactive: {
      backgroundColor: "#fef2f2",
      borderColor: "#fca5a5",
      color: "#b91c1c",
      "&:hover": { backgroundColor: "#fee2e2" },
    },
  },
} as const;

/** Activity type grid: `grid-cols-2 sm:cols-3 md:cols-4 gap-2`, buttons full width */
export const activityCreateActivityTypeGridSx: SxProps<Theme> = {
  display: "grid",
  gridTemplateColumns: {
    xs: "repeat(2, minmax(0, 1fr))",
    sm: "repeat(3, minmax(0, 1fr))",
    md: "repeat(4, minmax(0, 1fr))",
  },
  gap: 1,
  width: "100%",
};

export const activityCreateActivityTypeButtonSx = (selected: boolean): SxProps<Theme> => ({
  width: "100%",
  justifyContent: "flex-start",
  alignItems: "center",
  gap: 1,
  px: 1.5,
  py: 1,
  borderRadius: 1,
  border: "1px solid",
  fontSize: 14,
  fontWeight: 400,
  textTransform: "none",
  textAlign: "left",
  minHeight: 40,
  ...(selected
    ? {
        backgroundColor: "#9333ea",
        borderColor: "#9333ea",
        color: "#ffffff",
        boxShadow: "none",
        "&:hover": {
          backgroundColor: "#9333ea",
          borderColor: "#9333ea",
          color: "#ffffff",
          boxShadow: "none",
        },
      }
    : {
        backgroundColor: "#fff",
        borderColor: "#d1d5db",
        color: "#374151",
        "&:hover": { backgroundColor: "#f9fafb" },
      }),
});

export const activityCreateCheckboxBoxSx = (selected: boolean): SxProps<Theme> => ({
  width: 16,
  height: 16,
  borderRadius: 0.5,
  border: "1px solid",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  ...(selected
    ? {
        backgroundColor: "#fff",
        borderColor: "#fff",
      }
    : {
        backgroundColor: "transparent",
        borderColor: "#d1d5db",
      }),
});

export const activityCreateResponsePresetRowSx: SxProps<Theme> = {
  display: "flex",
  gap: 0.5,
  width: "100%",
};

export const activityCreateResponsePresetButtonSx = (active: boolean): SxProps<Theme> => ({
  flex: 1,
  borderRadius: 1,
  py: 0.75,
  px: 1,
  fontSize: 12,
  fontWeight: 400,
  textTransform: "none",
  border: "1px solid",
  minWidth: 0,
  ...(active
    ? {
        backgroundColor: "#2563eb",
        borderColor: "#2563eb",
        color: "#ffffff",
        boxShadow: "none",
        "&:hover": {
          backgroundColor: "#2563eb",
          borderColor: "#2563eb",
          color: "#ffffff",
          boxShadow: "none",
        },
      }
    : {
        backgroundColor: "#fff",
        borderColor: "#d1d5db",
        color: "#374151",
        "&:hover": { backgroundColor: "#f9fafb" },
      }),
});

export const activityCreateContentSx: SxProps<Theme> = {
  minHeight: 0,
  flex: 1,
  display: "flex",
  flexDirection: "column",
};

export const activityCreateFormSx: SxProps<Theme> = {
  minHeight: 0,
  overflow: "auto",
  pr: 0.5,
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

export const activityCreateCardSx: SxProps<Theme> = {
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 2,
  p: 2,
  backgroundColor: "#fff",
};

export const activityCreateSectionTitleSx: SxProps<Theme> = {
  fontSize: 14,
  fontWeight: 600,
  color: "text.secondary",
  mb: 1.5,
};

export const activityCreateLabelSx: SxProps<Theme> = {
  fontSize: 12,
  fontWeight: 600,
  color: "text.secondary",
  mb: 0.75,
};

/** Scoring fields: no nested bordered panel inside the question card. */
export const activityCreateScoringSectionSx: SxProps<Theme> = {
  border: "none",
  backgroundColor: "transparent",
  borderRadius: 0,
  p: 0,
  pt: 1,
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

/** Question Type + Max silence side-by-side (matches original builder). */
export const activityCreateQuestionSettingsGridSx: SxProps<Theme> = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
  gap: 2,
  alignItems: "flex-start",
};

export const activityCreateInsertBetweenRowSx: SxProps<Theme> = {
  display: "flex",
  flexWrap: "wrap",
  gap: 1,
  py: 1,
  justifyContent: "center",
};

export const activityCreateInsertInstructionButtonSx: SxProps<Theme> = {
  borderRadius: 1,
  px: 1,
  py: 0.5,
  fontSize: 12,
  fontWeight: 600,
  textTransform: "none",
  backgroundColor: "#dbeafe",
  color: "#1d4ed8",
  border: "none",
  "&:hover": {
    backgroundColor: "#bfdbfe",
  },
};

export const activityCreateInsertQuestionButtonSx: SxProps<Theme> = {
  borderRadius: 1,
  px: 1,
  py: 0.5,
  fontSize: 12,
  fontWeight: 600,
  textTransform: "none",
  backgroundColor: "#dcfce7",
  color: "#15803d",
  border: "none",
  "&:hover": {
    backgroundColor: "#bbf7d0",
  },
};

export const activityCreateBlockCardSx = (
  expanded: boolean,
  contentType: "INSTRUCTION" | "QUESTION" | undefined
): SxProps<Theme> => {
  const isInstruction = contentType === "INSTRUCTION";
  const isQuestion = contentType === "QUESTION";

  if (!expanded) {
    return {
      border: "2px solid",
      borderColor: "#e5e7eb",
      borderRadius: 2,
      backgroundColor: "#fafafa",
      overflow: "visible",
    };
  }

  if (isInstruction) {
    return {
      border: "2px solid",
      borderColor: "#93c5fd",
      borderRadius: 2,
      backgroundColor: "#eff6ff",
      overflow: "visible",
    };
  }

  if (isQuestion) {
    return {
      border: "2px solid",
      borderColor: "#6ee7b7",
      borderRadius: 2,
      backgroundColor: "#ecfdf5",
      overflow: "visible",
    };
  }

  return {
    border: "2px solid",
    borderColor: "#111827",
    borderRadius: 2,
    backgroundColor: "#fff",
    overflow: "visible",
  };
};

export const activityCreateBlockHeaderSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
  px: 1.5,
  py: 1,
  cursor: "pointer",
};

export const activityCreateBlockBodySx: SxProps<Theme> = {
  p: 2,
  pt: 0,
  display: "grid",
  gap: 1.25,
};

/** Layout only: default MUI outlined fields (no forced white fill on inputs). */
export const activityCreateBlockBodyInnerSx: SxProps<Theme> = {
  ...activityCreateBlockBodySx,
  pt: 2,
};

export const activityCreateAlertInlineSx: SxProps<Theme> = {
  mb: 1,
};

export const activityCreateActivityTypesLoadingWrapSx: SxProps<Theme> = {
  display: "flex",
  justifyContent: "center",
  py: 3,
};

export const activityCreateActivityTypeCheckIconSx: SxProps<Theme> = {
  fontSize: 14,
  color: "#9333ea",
};

export const activityCreateActivityTypeRowLabelSx: SxProps<Theme> = {
  flex: 1,
  textAlign: "left",
};

export const activityCreateExpectedAnswerRemoveIconSx: SxProps<Theme> = {
  color: "error.main",
};

export const activityCreateAddAnotherAnswerButtonSx: SxProps<Theme> = {
  alignSelf: "flex-start",
  textTransform: "none",
  fontWeight: 600,
  color: "primary.main",
};

export const activityCreateInsertRowIconSx: SxProps<Theme> = {
  fontSize: 14,
};

/** Content card heading (tighter bottom margin than metadata sections). */
export const activityCreateContentSectionTitleSx: SxProps<Theme> = {
  ...activityCreateSectionTitleSx,
  mb: 1,
};

export const activityCreateBlockHeaderTitleStackSx: SxProps<Theme> = {
  minWidth: 0,
};

export const activityCreateBlockHeaderPreviewTextSx: SxProps<Theme> = {
  flex: 1,
};

export const activityCreateEmptyContentPlaceholderSx: SxProps<Theme> = {
  border: "2px dashed",
  borderColor: "divider",
  borderRadius: 2,
  py: 6,
  textAlign: "center",
  color: "text.secondary",
};

export const activityCreateEmptyContentCaptionSx: SxProps<Theme> = {
  mt: 0.5,
};

export const activityCreateResetFormButtonSx: SxProps<Theme> = {
  borderRadius: 1,
};
